/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useCallback } from 'react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { Questionnaire } from '@userzoomplatform/pe-study-tasks';
import { QuestionnaireTaskData } from '@userzoomplatform/pe-study-tasks/dist/lib/types/task';
import { Texts } from '@userzoomplatform/pe-study-tasks/dist/lib/texts/types';
import { useTranslation } from 'react-i18next';

import { gqlQuestionTypes } from '../../../utils/constants';
import { TASKS_FIELDS, QUESTIONNAIRE_FIELDS } from '../fragments';
import { useToken } from '../../../hooks';
import config from '../../../utils/config';
import { Namespaces } from '../../../utils/i18n';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface Props {
	data: QuestionnaireTaskData;
	systemTexts: Texts['systemTexts'];
	studyName: string;
	setTask: (task: any) => any;
}

const defaultProps = {};

const answerPageMutation = gql`
	${TASKS_FIELDS}

	mutation ($answer: AnswerPage!) {
		answerPage(answer: $answer) {
			...TasksFields
		}
	}
`;

const backPageQuery = gql`
	${QUESTIONNAIRE_FIELDS}

	query ($questionnaireId: String!) {
		previousPage(questionnaireId: $questionnaireId) {
			...QuestionnaireFields
		}
	}
`;

const QuestionnaireTask = ({ data, setTask, systemTexts, studyName }: Props): ReactElement => {
	const token = useToken();
	const { assetsBasePath } = config();
	const { t } = useTranslation([Namespaces.Desktop]);

	const [answerPage] = useMutation<any>(answerPageMutation, {
		context: { headers: { authorization: `Bearer ${token}` } },
		onCompleted: (response: any) => {
			setTask(response?.answerPage);
		},
	});

	const [getPreviousPage] = useLazyQuery<any>(backPageQuery, {
		fetchPolicy: 'no-cache',
		context: { headers: { authorization: `Bearer ${token}` } },
		onCompleted: (response: any) => {
			setTask(response?.previousPage);
		},
	});

	const onAnswerPage = useCallback(
		async (answers: any) => {
			answerPage({
				variables: {
					answer: {
						pageId: data.page.number,
						questionnaireId: data.id,
						answers: answers.map(({ type, ...answer }: any) => ({
							type: gqlQuestionTypes[type],
							[gqlQuestionTypes[type]]: answer,
						})),
					},
				},
			});
		},
		[data, answerPage]
	);

	const onBackPage = useCallback(() => {
		getPreviousPage({ variables: { questionnaireId: data.id } });
	}, [data, getPreviousPage]);

	if (!data.page) {
		return <div className="questionnaire no-page" />;
	}

	const texts = {
		systemTexts,
		copies: {
			optionalQuestionLabel: t(`${Namespaces.Desktop}:common.optional`),
			backButton: t(`${Namespaces.Desktop}:common.back`),
			typeYourResponse: t(`${Namespaces.Desktop}:common.typeYourResponse`),
		},
	};

	return (
		<Questionnaire
			id={data.id}
			studyName={studyName}
			page={data.page}
			progress={data.progress}
			onAnswerPage={onAnswerPage}
			onBackPage={onBackPage}
			onExit={() => window.close()}
			texts={texts}
			config={{ assetsBasePath }}
		/>
	);
};

QuestionnaireTask.defaultProps = defaultProps;
export default QuestionnaireTask;
